// src/components/SEO/HeadSEO.jsx
import { useLocation } from "react-router-dom";
import { useSiteMetadata } from "@/hooks/useSiteMetadata";
import { useMemo, useEffect } from "react";

export const HeadSEO = ({
  siteTitle,
  siteDescription,
  siteImage,
  siteImageAlt,
  noindex = false,
  structuredData,
  additionalMeta = [],
  preloadImage, // LCP image imported from assets
  preloadImageSrcSet,
  preloadImageSizes,
}) => {
  const location = useLocation();
  const isArticle = location.pathname.startsWith("/articles/");

  const {
    siteTitle: defaultTitle,
    siteDescription: defaultDescription,
    siteImage: defaultImage,
    siteImageAlt: defaultImageAlt,
    siteUrl,
    siteLang,
    siteXUsername,
  } = useSiteMetadata();

  const seo = useMemo(
    () => ({
      title: siteTitle || defaultTitle,
      description: siteDescription || defaultDescription,
      image: siteImage ? `${siteUrl}${siteImage}` : `${siteUrl}${defaultImage}`,
      imageAlt: siteImageAlt || defaultImageAlt,
      url: `${siteUrl}${location.pathname}${location.search}`,
      canonicalUrl: `${siteUrl}${location.pathname}`,
    }),
    [
      siteTitle,
      siteDescription,
      siteImage,
      siteImageAlt,
      location.pathname,
      location.search,
      defaultTitle,
      defaultDescription,
      defaultImage,
      defaultImageAlt,
      siteUrl,
    ]
  );

  // Dynamically inject <link rel="preload"> for hero/LCP
  // useEffect(() => {
  //   if (!preloadImage) return;

  //   const existingLink = document.querySelector(
  //     `link[rel="preload"][href="${preloadImage}"]`
  //   );

  //   if (!existingLink) {
  //     const link = document.createElement("link");
  //     link.rel = "preload";
  //     link.as = "image";
  //     link.href = preloadImage;
  //     link.fetchpriority = "high";

  //     if (preloadImageSrcSet)
  //       link.setAttribute("imagesrcset", preloadImageSrcSet);
  //     if (preloadImageSizes) link.setAttribute("imagesizes", preloadImageSizes);

  //     document.head.appendChild(link);
  //   }

  //   return () => {
  //     // optional cleanup
  //     const link = document.querySelector(
  //       `link[rel="preload"][href="${preloadImage}"]`
  //     );
  //     if (link) document.head.removeChild(link);
  //   };
  // }, [preloadImage, preloadImageSrcSet, preloadImageSizes]);

  return (
    <>
      {/* Basic SEO */}
      <title>{`${seo.title} | ${defaultTitle}`}</title>
      <meta name="description" content={seo.description} />
      <meta name="image" content={seo.image} />
      <link rel="canonical" href={seo.canonicalUrl} />

      {/* Open Graph */}
      <meta property="og:title" content={seo.title} />
      <meta property="og:description" content={seo.description} />
      <meta property="og:image" content={seo.image} />
      <meta property="og:type" content={isArticle ? "article" : "website"} />
      <meta property="og:url" content={seo.canonicalUrl} />
      <meta property="og:site_name" content={defaultTitle} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={seo.title} />
      <meta name="twitter:description" content={seo.description} />
      <meta name="twitter:image" content={seo.image} />
      <meta name="twitter:image:alt" content={seo.imageAlt} />
      <meta name="twitter:creator" content={siteXUsername} />

      {/* Robots */}
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Structured Data */}
      {structuredData && !noindex && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}

      {/* Additional meta */}
      {additionalMeta.map((m, idx) => (
        <meta
          key={idx}
          {...(m.property ? { property: m.name } : { name: m.name })}
          content={m.content}
        />
      ))}
    </>
  );
};
